import React, { useState } from 'react';
import { RosaryIcon } from '../../icons/RosaryIcon';

interface NovenaExplorerScreenProps {
  onContinue: (data: { selectedNovena?: string }) => void;
}

interface NovenaOption {
  id: string;
  title: string;
  saint: string;
  duration: string;
  description: string;
  emoji: React.ReactNode;
}

const novenaOptions: NovenaOption[] = [
  {
    id: 'st-therese',
    saint: 'St. Thérèse of Lisieux',
    title: 'The Little Flower',
    duration: '9 days',
    description: 'For simple holiness and trust in God\'s love',
    emoji: <RosaryIcon size={48} className="text-indigo-600 dark:text-indigo-400" />,
  },
  {
    id: 'divine-mercy',
    saint: 'Divine Mercy',
    title: 'Jesus, I Trust in You',
    duration: '9 days',
    description: 'For God\'s mercy and forgiveness',
    emoji: '✝️',
  },
  {
    id: 'st-joseph',
    saint: 'St. Joseph',
    title: 'Protector and Provider',
    duration: '30 days',
    description: 'For protection, provision, and holy fatherhood',
    emoji: '🔨',
  },
  {
    id: 'sacred-heart',
    saint: 'Sacred Heart of Jesus',
    title: 'Heart of Divine Love',
    duration: '9 days',
    description: 'For devotion to the Sacred Heart and reparation',
    emoji: '❤️',
  },
];

export const NovenaExplorerScreen: React.FC<NovenaExplorerScreenProps> = ({ onContinue }) => {
  const [selectedNovena, setSelectedNovena] = useState<string | null>(null);

  const handleContinue = () => {
    onContinue({
      selectedNovena: selectedNovena || undefined,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="text-5xl mb-4">🙏</div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Discover beautiful traditional novenas
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Choose a novena to begin your prayer journey
        </p>
      </div>

      {/* Novena Grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {novenaOptions.map((novena) => (
            <button
              key={novena.id}
              onClick={() => setSelectedNovena(novena.id)}
              className={`
                p-6 rounded-2xl text-left transition-all duration-200 border-2 relative
                ${
                  selectedNovena === novena.id
                    ? 'border-indigo-600 ring-2 ring-indigo-600/20 bg-white dark:bg-gray-800 shadow-xl'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl hover:border-indigo-400 dark:hover:border-indigo-500'
                }
              `}
            >
              <div className="flex flex-col gap-3">
                {/* Icon and duration */}
                <div className="flex items-start justify-between">
                  <div className="text-4xl">{novena.emoji}</div>
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                    {novena.duration}
                  </span>
                </div>

                {/* Content */}
                <div>
                  <h3 className="font-bold text-lg mb-1 text-gray-900 dark:text-white">
                    {novena.saint}
                  </h3>
                  <p className="text-sm font-medium mb-2 text-indigo-600 dark:text-indigo-400">
                    {novena.title}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {novena.description}
                  </p>
                </div>

                {/* Selection indicator */}
                {selectedNovena === novena.id && (
                  <div className="flex items-center justify-end">
                    <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Continue Button */}
      <div className="mt-8">
        <button
          onClick={handleContinue}
          disabled={!selectedNovena}
          className={`
            w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform
            ${
              selectedNovena
                ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white hover:scale-105 shadow-lg'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Explore Novenas
        </button>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-3">
          You can explore other novenas anytime
        </p>
      </div>
    </div>
  );
};